<template>
  <Card>
    <CollapsibleSection
      buttonText="Membership suggestions"
      :class="$style.collapsibleSection"
      :isExpanded="toggleSection"
      @expand="handleToggleSection"
    >
      <template v-slot:content>
        <div>
          <p
            v-if="sectionText"
            :class="$style.sectionText"
          >
            {{ sectionText }}
          </p>
          <Checkbox
            v-if="!membershipSuggestionAutomation"
            :class="$style.checkboxContainerNarrow"
            :customStyles="{
              checkedLabel: $style.checkboxLabel,
            }"
            :indeterminate="false"
            label="Email me when membership updates are available"
            :value="item.memberSuggestionsNotify"
            @input="handleEmailSuggestionNotificationChange()"
          />
          <div>
            <ValidationObserver
              v-slot="{ invalid }"
            >
              <section
                :class="$style.filtersWrapper"
                :style="cssVars"
              >
                <header :class="$style.filtersHeader">
                  <div :class="$style.attributeTitle">
                    Attribute
                  </div>
                  <div :class="$style.operatorTitle">
                    Operator
                  </div>
                  <div :class="$style.valueTitle">
                    Value
                  </div>
                </header>
                <template v-if="hasMemberQueryFilters">
                  <MembershipSuggestionFilterRow
                    v-for="(filter, index) in item.memberQueryUI.filters"
                    :key="filter.id"
                    :attributes="migratedAttributes"
                    :class="$style.filterRow"
                    :filter="filter"
                    :filterIndex="index"
                    @addIncludesValue="handleAddIncludesItem"
                    @clearChildFilters="handleClearChildFilters"
                    @deleteFilter="handleDeleteListItem"
                    @updateFilter="handleChangeListItem"
                  />
                  <div :class="$style.exemptionListWrapper">
                    <SelectUserExemptionField
                      :key="selectFieldLabel"
                      :data-test-id="$testId('selectUserExemptionField')"
                      :label="selectFieldLabel"
                      :value="item.memberQueryExemptions"
                      @selectionChange="handleSelectValueChange($event, 'memberQueryExemptions')"
                    />
                  </div>
                </template>
                <template v-else>
                  <div :class="$style.noFilters">
                    You do not have any conditions configured.
                  </div>
                </template>
              </section>
              <div
                :class="$style.newLayoutButtonsWrapper"
              >
                <div>
                  <Link
                    :href="kbLink"
                    target="_blank"
                    text="Benefits of membership suggestions"
                  />
                </div>
                <div
                  :class="$style.newLayoutButtonsContainer"
                >
                  <Button
                    :data-test-id="$testId('addFilterButton')"
                    hasOutline
                    :isDisabled="invalid"
                    text="Add Condition"
                    type="secondary"
                    @click="handleAddFilter"
                  />
                  <Button
                    v-if="hasMemberQueryFilters && !isAutomationEnabled"
                    :data-test-id="$testId('reviewSuggestionsButton')"
                    :isDisabled="invalid"
                    text="review suggestions"
                    @click="handleReviewSuggestionsClick"
                  />
                </div>
              </div>
            </ValidationObserver>
          </div>
        </div>
      </template>
    </CollapsibleSection>
  </Card>
</template>

<script>
import {
  Button,
  Card,
  Checkbox,
  CollapsibleSection,
  Link,
} from '@jumpcloud/ui-components';
import { ValidationObserver } from 'vee-validate3';
import {
  addFilter,
  buildMemberQueryUI,
  deleteItemFromString,
  includesPipeCharacter,
  regexPipeCharacter,
  updateListItem,
} from 'client/util/membershipSuggestionsUtil';
import { cloneDeep, get } from 'lodash';
import { mapState } from 'vuex';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import GroupsConstants from 'client/util/Constants/GroupsConstants';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import MembershipSuggestionFilterRow from 'client/groups/User/MembershipSuggestionFilterRow.vue';
import SelectUserExemptionField from 'client/groups/User/SelectUserExemptionField.vue';
import Util from 'client/util/Util';

const { rulesType: attributeList } = GroupsConstants;
const attributeKeys = Object.keys(attributeList);
const { convertPascalCaseToWords } = Util;

export default {
  name: 'MembershipSuggestionRules',

  components: {
    Button,
    Card,
    Checkbox,
    CollapsibleSection,
    Link,
    MembershipSuggestionFilterRow,
    SelectUserExemptionField,
    ValidationObserver,
  },

  inject: ['$validator'],

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
  ],

  props: {
    handleReviewSuggestions: {
      default: () => {},
      type: Function,
    },
    isAutomationEnabled: {
      default: false,
      type: Boolean,
    },
  },

  data() {
    return {
      // Coordinates column sizes between headers and filter rows.
      cssVars: {
        '--columnSizes': 'minmax(70px, 1fr) 2fr minmax(122px, 1fr) 3fr 32px',
        '--columnGap': '20px',
      },
      // TODO: use a related kblink later.
      kbLink: KbArticleUrls.attributeDrivenGroupCreation,
      rulesType: [
        'Company',
        'Cost Center',
        'Department',
        'Description',
        'Employee Type',
        'Job Title',
        'Location',
      ],
      // This is for new membership suggestions
      migratedAttributes: cloneDeep(attributeKeys),
      toggleSection: true,
      sectionText: 'Inform group membership by setting relevant attribute value conditions for users of this group. View suggestions to review recommended membership updates.',
      selectFieldLabel: GroupsConstants.membershipSuggestions.exemptionFieldLabel,
    };
  },

  computed: {
    ...mapState('FeatureFlagsModule', {
      membershipSuggestionAutomation: state => state
        .betaFeatures.membershipSuggestionAutomation,
    }),

    /**
     * Get a brand new memberQuery object or current one if exists.
     * @returns {Object}
    * */
    getMemberQueryObject() {
      return this.item.memberQuery
        ? cloneDeep(this.item.memberQuery)
        : { filters: [], queryType: 'FilterQuery' };
    },

    /**
     * Since we are using memberQuery clone object (memberQueryUI), we
     * need to validate if there is data on it.
     * @returns {Boolean}
    * */
    hasMemberQueryFilters() {
      const hasMemberQueryFilters = !!get(this.item, 'memberQuery.filters', []).length;
      const hasMemberQueryUIFilters = !!get(this.item, 'memberQueryUI.filters', []).length;

      return hasMemberQueryFilters && hasMemberQueryUIFilters;
    },
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
  },

  mounted() {
    if (this.item.memberQuery) {
      const fieldsMapper = this.item.memberQuery.filters.map(rule => rule.field);

      fieldsMapper.forEach((item) => {
        const field = convertPascalCaseToWords(item, 'remove');
        const idx = this.rulesType.indexOf(field);

        this.rulesType.splice(idx, 1);
      });

      this.setItemField(
        {
          key: 'memberQueryUI',
          value: buildMemberQueryUI(this.item.memberQuery),
        },
      );

      this.pruneAttributesList();
    }
  },

  methods: {

    handleAddFilter() {
      const defaultFilter = {
        field: '',
        operator: 'eq',
        value: '',
      };
      const filters = this.item.memberQuery
        ? cloneDeep(this.item.memberQuery.filters)
        : [];

      filters.push(defaultFilter);

      this.setItemField(
        {
          key: 'memberQuery',
          value: {
            queryType: 'FilterQuery',
            filters,
          },
        },
      );

      this.setItemField(
        {
          key: 'memberQueryUI',
          value: addFilter(filters.length, this.item.memberQueryUI),
        },
      );
    },

    /**
     * Method for adding includes values/standard value to an existing filter.
     * On this method we need to update memberQuery object and clone it
     * in order to render data on membership suggestions table.
     * Eg. (Location Equals Denver ----- > Location In (Denver, Ohio))
     * @param {Int} indexToAdd
     * @param {Object} listItem
     * */
    handleAddIncludesItem(indexToAdd, listItem) {
      const memberQuery = this.getMemberQueryObject;

      const item = cloneDeep(listItem);

      const {
        operator, parentIndex, type, value,
      } = item;

      const isParentType = (type === 'parent');
      const isIncludesOperator = (operator === 'in');

      const parentIndexValue = this.item.memberQuery.filters[parentIndex].value;
      const alternativeParentIndexValue = memberQuery.filters[parentIndex].value;

      const formatParentValue = includesPipeCharacter(parentIndexValue || null)
        ? parentIndexValue : alternativeParentIndexValue || null;

      const includesPipeValidation = isParentType && (
        includesPipeCharacter(parentIndexValue)
      || includesPipeCharacter(alternativeParentIndexValue));

      const operatorValue = includesPipeValidation
        ? formatParentValue : value;

      const attributes = memberQuery.filters;

      delete item.id;
      delete item.isLastItem;
      delete item.parentIndex;
      delete item.parentIndexUI;
      delete item.type;

      if (isIncludesOperator) {
        const parentValue = formatParentValue || '|';
        item.value = isParentType ? `${operatorValue}|` : `${parentValue}|`;

        attributes[parentIndex] = { ...attributes[parentIndex], ...item };
      } else {
        attributes[indexToAdd] = { ...attributes[indexToAdd], ...item };
      }

      this.setItemField({
        key: 'memberQuery',
        value: {
          queryType: 'FilterQuery',
          filters: attributes,
        },
      });

      this.setItemField(
        {
          key: 'memberQueryUI',
          value: buildMemberQueryUI(this.item.memberQuery),
        },
      );
    },

    /**
     * Method for changing includes values/standard value to a existing filter.
     * On this method we need to update memberQuery object and clone it
     * in order to render data on membership suggestions table.
     * Eg. (Location Equals Denver ----- > Location Equals FLorida
     * Eg. (Location In (Denver|Ohio) ----- > Location In (Kansas, Ohio))
     * @param {Int} indexToChange
     * @param {Object} listItem
     * */
    handleChangeListItem(indexToChange, listItem) {
      const memberQuery = this.getMemberQueryObject;
      const item = cloneDeep(listItem);
      const {
        field, parentIndex, parentIndexUI, type, value, operator,
      } = item;
      const isParentType = type === 'parent';
      // we want to verify if the value includes '|' characters in order to support it.
      // if there are pipes then we are going to use a character wildcard (¿?) for next steps.
      const operatorValue = includesPipeCharacter(value)
        ? value.split('|').join('¿?') : value;
      const attributes = memberQuery.filters;

      // as we did on operatorValue , we want to verify if '|' characters exist.
      const parentValue = includesPipeCharacter(attributes[parentIndex].value)
        ? attributes[parentIndex].value.split('|').join('¿?')
        : attributes[parentIndex].value;

      if (isParentType) {
        delete item.id;
        delete item.isLastItem;
        delete item.parentIndex;
        delete item.parentIndexUI;
        delete item.type;

        // here we want to clean those values that have the character wildcard
        // with escapes, and also get all the values by splitting the array
        // using the wildcard.
        const newParentOperatorValue = parentValue.includes('\\¿?')
          ? regexPipeCharacter(attributes[parentIndex].value)
          : parentValue.split('¿?');

        newParentOperatorValue[0] = operatorValue;

        attributes[parentIndex].field = field;
        attributes[parentIndex].operator = operator;

        // when we finished processing all the relevant data, then we want
        // to join again by pipes and replace all wildcards with pipes with escapes.
        // (We need this due to a backend api support).
        const parentOperatorValueWithPipes = newParentOperatorValue.join('|').replace('¿?', '\\|');

        // then we need to check if there are extra pipes on the string, if that is the case
        // we want to delete it.(respecting the rules of keep pipes with escapes, of course).
        const formatParentOperatorValue = parentOperatorValueWithPipes.endsWith('|')
          && parentOperatorValueWithPipes[parentOperatorValueWithPipes.length - 2] !== '\\'
          ? parentOperatorValueWithPipes.substring(0, parentOperatorValueWithPipes.length - 1)
          : parentOperatorValueWithPipes;

        // finally we assign the final value to the corresponding filter in the array.
        attributes[parentIndex].value = formatParentOperatorValue.split('¿?').join('\\|');
      } else {
        // split values with pipe (|).
        // Eg. test1|test2

        const formatValue = parentValue.split('¿?').join('|');
        const refactorValue = formatValue.endsWith('|') ? `${formatValue} ` : formatValue;
        let newOperatorValue = regexPipeCharacter(refactorValue);

        // if max index in split array is 1, that means we do not need to
        // do a big effort by looking for the real index of the data.
        if ((newOperatorValue.length - 1) === 1) {
          // values are set with the value we want to change and then
          // we construct the value again
          // Eg. test1|test2 ---> value: test100, index: 1 ---> result: test1|test100

          newOperatorValue[1] = operatorValue;
        } else {
          // Else, we need to calculate what the real index is on memberquery object vs
          // memberqwuery clone, if there are the same, then we return the value of that change
          // if not, we return the value the same as it is.
          // we construct the value again.

          newOperatorValue = newOperatorValue.map((element, indexToCompare) => {
            const isIndexOfValueToChange = Math.abs(
              parentIndexUI - (indexToChange + 1),
            ) === indexToCompare;

            if (isIndexOfValueToChange) return operatorValue;
            return element;
          });
        }

        // when we finished processing all the relevant data, then we want
        // to join again by pipes and replace all wildcards with pipes with escapes.
        // (We need this due to a backend api support).
        const operatorValueWithPipes = newOperatorValue.join('|');

        const joinWithPipes = operatorValueWithPipes.split('¿?').join('|');

        const joinWithEscapedPipes = operatorValueWithPipes.split('¿?').join('\\|');

        const cleanParentOperatorValue = operatorValueWithPipes.includes('\\¿?')
          ? joinWithPipes
          : joinWithEscapedPipes;

        // then we need to check if there are extra pipes on the string, if that is the case
        // we want to delete it.(respecting the rules of keep pipes with escapes, of course).

        const pipeOnValueCheck = cleanParentOperatorValue.endsWith('|')
          && cleanParentOperatorValue[cleanParentOperatorValue.length - 2] !== '\\';

        const pipeOnValueRemove = cleanParentOperatorValue
          .substring(0, cleanParentOperatorValue.length - 1);

        const formatParentOperatorValue = pipeOnValueCheck
          ? pipeOnValueRemove
          : cleanParentOperatorValue;

        // finally we assign the final value to the corresponding filter in the array.
        attributes[parentIndex].value = formatParentOperatorValue;
      }

      this.setItemField({
        key: 'memberQuery',
        value: {
          queryType: 'FilterQuery',
          filters: attributes,
        },
      });

      this.setItemField(
        {
          key: 'memberQueryUI',
          value: updateListItem(listItem, this.item.memberQueryUI),
        },
      );

      this.pruneAttributesList();
    },

    handleClearChildFilters(filterIndex, parentIndexUI) {
      const memberQueryUIFilters = cloneDeep(this.item.memberQueryUI.filters); // clone
      const memberQueryFilters = cloneDeep(this.item.memberQuery.filters); // clone
      const parentFilter = memberQueryFilters[filterIndex];
      const parentFilterValue = parentFilter.value.split('|')[0];

      // resets to single value
      parentFilter.value = parentFilterValue;
      this.setItemField({
        key: 'memberQuery',
        value: {
          queryType: 'FilterQuery',
          filters: memberQueryFilters,
        },
      });
      this.setItemField(
        {
          key: 'memberQueryUI',
          value: {
            queryType: 'FilterQuery',
            filters: memberQueryUIFilters
              .filter((filter) => !(filter.type === 'child' && filter.parentIndexUI === parentIndexUI)),
          },
        },
      );
    },

    /**
     * Method for deleting includes values/standard value to a existing filter.
     * On this method we need to update memberQuery object and clone it
     * in order to render data on membership suggestions table.
     * Eg. (Location Equals Denver ----- >
     * Eg. (Location In (Denver|Ohio) ----- > Location Equals Denver
     * @param {Int} indexToDelete
     * @param {Object} listItem
     * */
    handleDeleteListItem(indexToDelete, listItem) {
      const memberQuery = this.getMemberQueryObject;
      const { filters } = this.item.memberQueryUI;
      const attributes = memberQuery.filters;
      const {
        id, parentIndex, type,
      } = listItem;

      if (type === 'parent') {
        attributes.splice(parentIndex, 1);
      } else if (type === 'child') {
        const parentAttributes = attributes[parentIndex];
        const parentValue = parentAttributes.value;
        const relatedItemsToParent = filters.filter((item) => item.parentIndex === parentIndex);
        const newIndexToDelete = relatedItemsToParent.findIndex((item) => item.id === id);
        const isLastChildToDelete = relatedItemsToParent.filter((item) => item.type === 'child').length <= 1;

        const operatorValue = deleteItemFromString(parentValue, newIndexToDelete);

        attributes[parentIndex].value = operatorValue;

        if (!operatorValue.includes('|') || isLastChildToDelete) attributes[parentIndex].operator = 'eq';
      }

      this.setItemField({
        key: 'memberQuery',
        value: {
          queryType: 'FilterQuery',
          filters: attributes,
        },
      });

      this.setItemField(
        {
          key: 'memberQueryUI',
          value: buildMemberQueryUI(this.item.memberQuery),
        },
      );

      this.pruneAttributesList();
    },

    handleEmailSuggestionNotificationChange() {
      this.setItemField({
        key: 'memberSuggestionsNotify',
        value: !this.item.memberSuggestionsNotify,
      });
    },

    handleReviewSuggestionsClick() {
      const payload = {
        action: 'click',
        event: 'Review suggestions button click',
        type: 'Membership Suggestions',
      };
      EventHelper.triggerMembershipSuggestionsButtonClicked(payload);
      this.handleReviewSuggestions();
    },

    handleSelectValueChange(value, key) {
      const newValue = value.map((id) => ({ type: 'USER', id }));
      this.setItemField({ key, value: newValue });
    },

    handleToggleSection() {
      this.toggleSection = !this.toggleSection;
    },

    pruneAttributesList() {
      // refresh list in the event of change/delete
      const newAttributes = cloneDeep(attributeKeys);

      this.item.memberQuery.filters.forEach(filter => {
        const attributeIndex = newAttributes.indexOf(filter.field);

        if (attributeIndex > -1) {
          newAttributes.splice(attributeIndex, 1);
        }
      });

      this.migratedAttributes = newAttributes;
    },
  },
};
</script>
<style module>
@import '../../components/FormValidation/css/formFields.css';

/** Need this in order to override default jumpcloud button/ collapsible section style */
.collapsibleSection > div {
  overflow: unset !important;
}

.checkboxContainerNarrow {
  margin: 22px 0 20px;
}

.checkboxLabel {
  font-weight: 500;
}

/** Need this in order to override default jumpcloud button/ collapsible section style */
.collapsibleSection button:first-child {
  margin-bottom: 0 !important;
}

.exemptionListWrapper {
  border-top: 1px dashed var(--jcGrey300);
  padding: 1.563rem 0 0 0;
}

.extraButtonContainer {
  margin-left: 10px;
  margin-top: 20px;
}

.hideComponent {
  display: none !important;
}

.newLayoutButtonsContainer {
  align-items: center;
}

.newLayoutButtonsContainer > :not(:last-child) {
  margin-right: 10px;
}

.newLayoutButtonsWrapper {
  align-items: center;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.sectionText {
  margin-top: 1.125rem;
}

.filtersWrapper {
  border-bottom: 1px solid #c9cccf;
  margin-bottom: 24px;
  padding-bottom: 24px;
}

.filtersHeader {
  border-bottom: 1px solid #c9cccf;
  display: grid;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  gap: var(--columnGap);
  grid-template-columns: var(--columnSizes);
  line-height: 17px;
  margin-bottom: 24px;
  padding-bottom: 4px;
}

.attributeTitle {
  grid-column: 2;
}

.operatorsTitle {
  grid-column: 3;
}

.valueTitle {
  grid-column: 4;
}

.filterRow {
  margin-bottom: 24px;
}

.filterRow:last-child {
  margin-bottom: 0;
}

.noFilters {
  font-size: 14px;
  font-style: italic;
  text-align: center;
}

.selectMarginTop {
  margin-top: 24px;
}
</style>
